import {
  getClientBuildMeta,
  getSentryReleaseName
} from "@pathwright/utils/src/client-build/clientBuildMeta"
import { getEnv } from "@pathwright/utils/src/env/env"
import { useTranslate } from "../lng/useTranslate"
import {
  type BrowserOptions,
  browserTracingIntegration,
  captureException,
  captureMessage,
  init,
  replayIntegration,
  setUser,
  withScope
} from "@sentry/browser"
import React, { useEffect } from "react"

export { captureException, captureMessage }

let sentryInitialized = false

export type SentryErrorProps = {
  children?: React.ReactNode
  user?: {
    id: string | number
    email?: string | null
  } | null
}

const SentryError: React.FC<SentryErrorProps> = ({ children, user }) => {
  const { t } = useTranslate()

  const buildMeta = getClientBuildMeta()
  const releaseOption = buildMeta.buildId
    ? { release: getSentryReleaseName(buildMeta.buildId) }
    : {}
  const buildTagOption = buildMeta.buildId
    ? { initialScope: { tags: { client_build_id: buildMeta.buildId } } }
    : {}

  const ignoreErrors = [
    // https://stackoverflow.com/a/50387233/6362803
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications.",
    // TODO: actually prevent the code from throwing this Uncaught(in promise) error
    t("auth.sign_in_form.error_didnt_recognize"),
    // // CodelyTV uses a Headway script and often see related errors
    // /Headway/
    // Erorr thrown by some 3rd party https://github.com/getsentry/sentry-javascript/issues/3440
    "Non-Error promise rejection captured",
    // Errors thown by some 3rd party.
    "The play() request was interrupted by a call to pause()",
    "Connection to Indexed Database server lost. Refresh the page to try again",
    "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection"
  ]

  // const denyUrls = [
  //   // other school integrations throwing errors we don't care about
  //   /https:\/\/api\.reftagger\.com\//i,
  //   /https:\/\/js\.intercomcdn\.com\//i,
  //   /https:\/\/munchkin\.marketo\.net\//i,
  //   /https:\/\/s\.adroll\.com\//i,
  //   /https:\/\/cdn\.livechatinc\.com\//i
  // ]

  // Expecting that the allowUrls should remove our need of using denyUrls and possibly beforeSend.
  const allowUrls = [
    // Attempting to only send those errors to Sentry which occur in our JS files.
    /https?:\/\/(prod|staging|testing|dev)-cdn.pathwright.com\/static\/js\//i
  ]

  // Optionally filter out events.
  // NOTE: allowUrls only applies if the event has a url to parse, so we must manually
  // filter out those events that do not have a url.
  const beforeSend: NonNullable<BrowserOptions["beforeSend"]> = (event) => {
    // Ignore all events occurring from file urls.
    if (window.location.pathname.startsWith("file:///")) {
      return null
    }

    const ignoredFiles = [
      // Ignore all events whose final frame source is anonymous.
      "<anonymous>",
      // Ignore 3rd part script.
      "profitwell.js"
    ]

    const frames = event.exception?.values?.[0]?.stacktrace?.frames
    const frame = frames?.[frames.length - 1]
    if (frame?.filename && ignoredFiles.includes(frame.filename)) {
      return null
    }

    return event
  }

  // Sentry must only be initialized once: Replay throws if a second instance is created.
  if (!sentryInitialized) {
    sentryInitialized = true
    init({
      ...releaseOption,
      ...buildTagOption,
      dsn: "https://f43b2d3cb8aa4924bab5f3987d2f913c@o99.ingest.sentry.io/5438",
      environment: getEnv(),
      // Propagates the trace ID to GraphQL and the backend so client and server errors share a trace.
      // tracesSampleRate stays unset: trace IDs only, no performance spans.
      integrations: [browserTracingIntegration(), replayIntegration()],
      tracePropagationTargets: [
        /^https:\/\/gql\.pathwright(staging|dev)?\.com\//,
        // Matched against the path of same-origin requests only (school domain backend API).
        /^\/api\//
      ],
      // Replays are recorded only for sessions that send an error.
      // TODO: drop to 0.05 after the 14-day unlimited trial. The Team plan includes 500
      // replays a month, and ~6-10k sessions a month hit an error; overage draws from
      // the shared pay-as-you-go budget that error overages also need.
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      ignoreErrors,
      // denyUrls,
      allowUrls,
      beforeSend
    })
  }

  // scope errors to current user
  useEffect(() => {
    if (user) {
      setUser({
        id: user.id,
        email: user.email ?? undefined,
        ip_address: "{{auto}}"
      })
    } else {
      withScope((scope) => scope.setUser(null))
    }
  }, [user])

  return <>{children}</>
}

SentryError.displayName = "SentryError"

export default SentryError
